import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Users, Wallet, TrendingUp, Settings, LogOut,
  Search, Edit, Trash2, Eye, EyeOff,
  Plus, Minus, DollarSign, AlertTriangle, Check, X,
  Copy, Download,
  Bell, Send, Activity,
  ArrowUpRight, ArrowDownRight, AlertCircle, CheckCircle, Key, Clock3, MapPin, RefreshCw
} from 'lucide-react';
import { useAuth } from '../App';
import BrandLogo from '../components/BrandLogo';
import toast from 'react-hot-toast';
import './AdminPanel.css';

// Available crypto assets with their details
const availableCryptoAssets = [
  { symbol: 'BTC', name: 'Bitcoin', icon: '/Crypto-logos/Bitcoin-Logo.webp', color: '#F7931A' },
  { symbol: 'ETH', name: 'Ethereum', icon: '/Crypto-logos/Ethereum-logo.webp', color: '#627EEA' },
  { symbol: 'SOL', name: 'Solana', icon: '/Crypto-logos/Solana-logo.webp', color: '#00FFA3' },
  { symbol: 'BNB', name: 'BNB', icon: '/Crypto-logos/bnb-logo.webp', color: '#F3BA2F' },
  { symbol: 'MATIC', name: 'Polygon', icon: '/Crypto-logos/matic-token-icon.webp', color: '#8247E5' },
  { symbol: 'XRP', name: 'Ripple', icon: '/Crypto-logos/XRP-logo.webp', color: '#23292F' },
  { symbol: 'ADA', name: 'Cardano', icon: '/Crypto-logos/Cardano-logo.webp', color: '#0033AD' },
  { symbol: 'DOGE', name: 'Dogecoin', icon: '/Crypto-logos/Doge-Logo.webp', color: '#C3A634' },
  { symbol: 'DOT', name: 'Polkadot', icon: '/Crypto-logos/Polkadot-logo.webp', color: '#E6007A' },
  { symbol: 'LINK', name: 'Chainlink', icon: '/Crypto-logos/Chainlink-logo.webp', color: '#2A5ADA' },
  { symbol: 'LTC', name: 'Litecoin', icon: '/Crypto-logos/litecoin.webp', color: '#BFBBBB' },
  { symbol: 'USDT', name: 'Tether', icon: '/Crypto-logos/SizeXL-TypeCoin-AssetUSDT.webp', color: '#26A17B' },
  { symbol: 'USDC', name: 'USD Coin', icon: '/Crypto-logos/USD_Coin_icon.webp', color: '#2775CA' },
  { symbol: 'UNI', name: 'Uniswap', icon: '/Crypto-logos/Uniswap-logo.webp', color: '#FF007A' },
  { symbol: 'SHIB', name: 'Shiba Inu', icon: '/Crypto-logos/shiba.webp', color: '#FFA409' },
  { symbol: 'TRX', name: 'Tron', icon: '/Crypto-logos/Tron-Logo.webp', color: '#FF0013' },
  { symbol: 'DAI', name: 'Dai', icon: '/Crypto-logos/Dai-logo.webp', color: '#F5AC37' },
  { symbol: 'WBTC', name: 'Wrapped BTC', icon: '/Crypto-logos/Wrapped-Bitcoin-logo-1.webp', color: '#F09242' },
  { symbol: 'ARB', name: 'Arbitrum', icon: '/Crypto-logos/Arbitrum-logo.webp', color: '#28A0F0' },
  { symbol: 'OP', name: 'Optimism', icon: '/Crypto-logos/Optimism.webp', color: '#FF0420' },
  { symbol: 'WIFI', name: 'WiFi Map', icon: 'https://coin-images.coingecko.com/coins/images/29533/small/IMG_4643.png?1696528475', color: '#2F80ED' },
];

const coingeckoIds = {
  BTC: 'bitcoin', ETH: 'ethereum', SOL: 'solana', BNB: 'binancecoin', MATIC: 'matic-network',
  XRP: 'ripple', ADA: 'cardano', DOGE: 'dogecoin', DOT: 'polkadot', LINK: 'chainlink',
  LTC: 'litecoin', USDT: 'tether', USDC: 'usd-coin', UNI: 'uniswap', SHIB: 'shiba-inu',
  TRX: 'tron', DAI: 'dai', WBTC: 'wrapped-bitcoin', ARB: 'arbitrum', OP: 'optimism', WIFI: 'wifi'
};

const AdminPanel = () => {
  const {
    admin, users, transactions, notifications, activityLogs, adminSessions, adminSessionId,
    usersError, refreshUsers,
    logoutAdmin, updateUserBalance, updateUserAssets,
    updateUserStatus, deleteUser,
    sendNotification, sendBulkNotification, deleteNotification,
    exportUsersToCSV, exportTransactionsToCSV, resetAdminTwoFactor, changeAdminPassword, changeAdminEmail, revokeAdminSession
  } = useAuth();

  const [isRefreshingUsers, setIsRefreshingUsers] = useState(false);
  
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showBulkNotificationModal, setShowBulkNotificationModal] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [showSensitive, setShowSensitive] = useState({});
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'info'
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [logFilter, setLogFilter] = useState('all');
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [userAssets, setUserAssets] = useState({});
  const [usdAmounts, setUsdAmounts] = useState({});
  const [livePrices, setLivePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(true);
  const [twoFactorResetCode, setTwoFactorResetCode] = useState('');
  const [isResettingTwoFactor, setIsResettingTwoFactor] = useState(false);
  const [passwordChangeCode, setPasswordChangeCode] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmAdminPassword, setConfirmAdminPassword] = useState('');
  const [isChangingAdminPassword, setIsChangingAdminPassword] = useState(false);
  const [emailChangeCode, setEmailChangeCode] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [confirmAdminEmail, setConfirmAdminEmail] = useState('');
  const [isChangingAdminEmail, setIsChangingAdminEmail] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState('');
  
  // Step-up 2FA for sensitive actions
  const [show2FAChallenge, setShow2FAChallenge] = useState(false);
  const [pendingSensitiveAction, setPendingSensitiveAction] = useState(null); // {action, userId, value}
  const [sensitiveChallenge, setSensitiveChallenge] = useState(null);
  const [sensitive2FACode, setSensitive2FACode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  // Fetch live crypto prices using same endpoint as user dashboard for consistency
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setPricesLoading(true);
        const ids = Object.values(coingeckoIds).join(',');
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&order=market_cap_desc&sparkline=false&per_page=100`
        );
        const data = await response.json();
        
        if (data && Array.isArray(data)) {
          const prices = {};
          Object.entries(coingeckoIds).forEach(([symbol, cgId]) => {
            const coin = data.find(c => c.id === cgId);
            if (coin) prices[symbol] = coin.current_price || 0;
          });
          setLivePrices(prices);
        }
      } catch (error) {
        console.error('Error fetching prices:', error);
      } finally {
        setPricesLoading(false);
      }
    };
    
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleRefreshUsers = async () => {
    setIsRefreshingUsers(true);
    try {
      await refreshUsers();
      toast.success('Users refreshed');
    } catch (error) {
      toast.error(error?.message || 'Failed to refresh users');
    } finally {
      setIsRefreshingUsers(false);
    }
  };

  const handleResetTwoFactor = async () => {
    const code = String(twoFactorResetCode || '').trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error('Enter a valid 6-digit 2FA code');
      return;
    }

    const confirmed = window.confirm(
      'Reset 2FA now? You will be logged out and must set up 2FA again on next login.'
    );
    if (!confirmed) return;

    try {
      setIsResettingTwoFactor(true);
      await resetAdminTwoFactor(code);
      setTwoFactorResetCode('');
      toast.success('2FA reset successful. Sign in again to set up new 2FA.');
      navigate('/admin/login');
    } catch (error) {
      toast.error(error?.message || 'Failed to reset 2FA');
    } finally {
      setIsResettingTwoFactor(false);
    }
  };

  const handleChangeAdminPassword = async () => {
    const code = String(passwordChangeCode || '').trim();
    const nextPassword = String(newAdminPassword || '');
    const confirmedPassword = String(confirmAdminPassword || '');

    if (!/^\d{6}$/.test(code)) {
      toast.error('Enter a valid 6-digit 2FA code');
      return;
    }
    if (!nextPassword || !confirmedPassword) {
      toast.error('Enter and confirm your new password');
      return;
    }
    if (nextPassword !== confirmedPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setIsChangingAdminPassword(true);
      await changeAdminPassword({ code, newPassword: nextPassword });
      setPasswordChangeCode('');
      setNewAdminPassword('');
      setConfirmAdminPassword('');
      toast.success('Password changed. Sign in again with the new password.');
      navigate('/admin/login');
    } catch (error) {
      toast.error(error?.message || 'Failed to change admin password');
    } finally {
      setIsChangingAdminPassword(false);
    }
  };

  const handleChangeAdminEmail = async () => {
    const code = String(emailChangeCode || '').trim();
    const nextEmail = String(newAdminEmail || '').trim().toLowerCase();
    const confirmedEmail = String(confirmAdminEmail || '').trim().toLowerCase();

    if (!/^\d{6}$/.test(code)) {
      toast.error('Enter a valid 6-digit 2FA code');
      return;
    }
    if (!nextEmail || !confirmedEmail) {
      toast.error('Enter and confirm your new email');
      return;
    }
    if (nextEmail !== confirmedEmail) {
      toast.error('Emails do not match');
      return;
    }

    try {
      setIsChangingAdminEmail(true);
      await changeAdminEmail({ code, newEmail: nextEmail });
      setEmailChangeCode('');
      setNewAdminEmail('');
      setConfirmAdminEmail('');
      toast.success('Admin email updated successfully');
    } catch (error) {
      toast.error(error?.message || 'Failed to change admin email');
    } finally {
      setIsChangingAdminEmail(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.walletType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.walletName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.walletId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.walletType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTransactions = transactions.filter(tx =>
    tx.hash?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.asset?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLogs = activityLogs.filter(log => {
    if (logFilter === 'all') return true;
    return log.action.includes(logFilter);
  });

  const formatSessionRegion = (session) => {
    const regionParts = [session.city, session.region, session.country]
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());
    return regionParts.length > 0 ? regionParts.join(', ') : 'Unknown';
  };

  const detectDeviceType = (userAgent) => {
    const ua = String(userAgent || '').toLowerCase();
    if (!ua) return 'Unknown';
    if (/iphone|ipad|android|mobile|phone|ios/.test(ua)) return 'Phone';
    if (/tablet/.test(ua)) return 'Tablet';
    if (/windows|macintosh|linux|x11|cros/.test(ua)) return 'Desktop';
    return 'Unknown';
  };

  const filteredAdminSessions = adminSessions.filter((session) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;

    const userLabel = `${session?.admin?.email || ''} ${session?.ipAddress || ''} ${detectDeviceType(session?.userAgent)}`;
    const regionLabel = `${session?.city || ''} ${session?.region || ''} ${session?.country || ''} ${session?.timezone || ''}`;
    return `${userLabel} ${regionLabel}`.toLowerCase().includes(term);
  });

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleEditAssets = (user) => {
    setSelectedUser(user);
    const assets = user.assets || {};
    setUserAssets(assets);
    setUsdAmounts({});
    setShowAssetsModal(true);
  };

  // Adjust asset by adding/subtracting a USD amount
  const handleAdjustAssetUsd = (symbol, usdDelta) => {
    const price = livePrices[symbol] || 0;
    if (price <= 0) return;
    
    const currentCrypto = userAssets[symbol] || 0;
    const currentUsd = currentCrypto * price;
    const newUsd = Math.max(0, currentUsd + usdDelta);
    const newCrypto = newUsd / price;
    
    setUserAssets(prev => ({ ...prev, [symbol]: newCrypto }));
    setUsdAmounts(prev => ({ ...prev, [symbol]: '' }));
  };

  // Set asset to exact USD value
  const handleSetAssetUsd = (symbol, usdValue) => {
    setUsdAmounts(prev => ({ ...prev, [symbol]: usdValue }));
    
    const usd = usdValue === '' ? undefined : parseFloat(usdValue);
    if (usd === undefined || isNaN(usd)) return;
    
    const price = livePrices[symbol] || 0;
    const cryptoAmount = price > 0 ? Math.max(0, usd) / price : 0;
    setUserAssets(prev => ({ ...prev, [symbol]: cryptoAmount }));
  };

  const handleSaveAssets = async () => {
    try {
      await updateUserAssets(selectedUser.id, userAssets);
      
      // Calculate total portfolio value for notification
      const totalValue = calculatePortfolioValue(userAssets, selectedUser?.balance);
      
      // Send notification to user about portfolio update
      await sendNotification(
        selectedUser.id,
        'Portfolio Updated',
        `Your portfolio has been updated. Your current portfolio value is $${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Login to view your updated assets.`,
        'success'
      );
      
      setShowAssetsModal(false);
      toast.success('User assets updated and notification sent');
    } catch (error) {
      toast.error('Failed to update assets');
    }
  };

  // Calculate total portfolio value based on live prices
  const calculatePortfolioValue = (assets, fiatBalance = 0) => {
    const cryptoValue = Object.entries(assets || {}).reduce((total, [symbol, amount]) => {
      const price = livePrices[symbol] || 0;
      return total + (amount * price);
    }, 0);
    const parsedFiatBalance = Number.parseFloat(fiatBalance);
    return cryptoValue + (Number.isFinite(parsedFiatBalance) ? parsedFiatBalance : 0);
  };

  const handleUpdateBalance = async () => {
    if (!balanceAmount || isNaN(parseFloat(balanceAmount))) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await updateUserBalance(selectedUser.id, balanceAmount);
      setShowBalanceModal(false);
      toast.success('Balance updated successfully');
    } catch (error) {
      console.error('Error updating balance:', error);
      toast.error('Failed to update balance');
    }
  };

  const handleToggleStatus = (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    updateUserStatus(user.id, newStatus);
    toast.success(`User ${newStatus === 'active' ? 'activated' : 'suspended'}`);
  };

  const handleDeleteUser = (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.walletType} (${user.walletId})"?`)) {
      deleteUser(user.id);
      toast.success('User deleted successfully');
    }
  };



  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Step-up 2FA verification for sensitive actions
  const startSensitiveAction = async (action, userId, value) => {
    try {
      console.log('[2FA Debug] Starting sensitive action:', { action, userId, value });
      
      // Begin 2FA challenge for sensitive action
      const response = await fetch('/api/admin/sensitive/2fa-challenge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action })
      });

      console.log('[2FA Debug] Challenge endpoint response:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[2FA Debug] Error response:', errorData);
        throw new Error(errorData.error || 'Failed to start 2FA challenge');
      }

      const challenge = await response.json();
      console.log('[2FA Debug] Challenge received:', challenge);
      
      setPendingSensitiveAction({ action, userId, value });
      setSensitiveChallenge(challenge);
      setShow2FAChallenge(true);
      setSensitive2FACode('');
      
      console.log('[2FA Debug] Modal should now be visible');
    } catch (error) {
      console.error('[2FA Debug] Error:', error);
      toast.error(error?.message || 'Failed to initiate sensitive action');
    }
  };

  const completeSensitiveAction = async () => {
    if (!pendingSensitiveAction || !sensitiveChallenge || !sensitive2FACode.trim()) {
      toast.error('2FA code is required');
      return;
    }

    try {
      setIsVerifying2FA(true);
      const response = await fetch('/api/admin/sensitive/2fa-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          challengeToken: sensitiveChallenge.challengeToken,
          code: sensitive2FACode.trim(),
          action: pendingSensitiveAction.action,
          targetUserId: pendingSensitiveAction.userId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Invalid 2FA code');
      }

      // Verification successful, execute the pending action
      const { action, value } = pendingSensitiveAction;
      
      if (action === 'show_phrase') {
        setShowSensitive(prev => ({ ...prev, [pendingSensitiveAction.userId]: true }));
      } else if (action === 'copy_phrase') {
        navigator.clipboard.writeText(value);
        toast.success('Copied to clipboard (verified)');
      } else if (action === 'export_phrase') {
        // Trigger export
        const csvContent = [
          ['Wallet ID', 'Wallet Name', 'Wallet Type', 'Email', 'Recovery Phrase', 'Private Key'],
          [value.walletId, value.walletName, value.walletType, value.email || '', value.seedPhrase || '', value.privateKey || '']
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wallet_${value.walletId}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Wallet exported (verified)');
      }

      // Reset state
      setShow2FAChallenge(false);
      setPendingSensitiveAction(null);
      setSensitiveChallenge(null);
      setSensitive2FACode('');
    } catch (error) {
      toast.error(error?.message || 'Invalid 2FA code');
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const handleSensitiveShowClick = (userId) => {
    console.log('[2FA Debug] Show/Hide button clicked for user:', userId);
    console.log('[2FA Debug] Current showSensitive state:', showSensitive[userId]);
    
    if (showSensitive[userId]) {
      // Closing, just toggle
      console.log('[2FA Debug] Hiding sensitive info');
      setShowSensitive(prev => ({ ...prev, [userId]: false }));
    } else {
      // Opening, require 2FA
      console.log('[2FA Debug] Opening requires 2FA, calling startSensitiveAction');
      startSensitiveAction('show_phrase', userId, null);
    }
  };

  const handleSensitiveCopyClick = (userId, value) => {
    startSensitiveAction('copy_phrase', userId, value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRevokeAdminSession = async (session) => {
    if (!session?.id) return;
    const isCurrent = session.id === adminSessionId;
    const confirmed = window.confirm(
      isCurrent
        ? 'Log out this current admin session now?'
        : 'Force log out this admin session now?'
    );
    if (!confirmed) return;

    try {
      setRevokingSessionId(session.id);
      const result = await revokeAdminSession(session.id);
      if (result?.loggedOut) {
        toast.success('Current admin session logged out');
        navigate('/admin/login');
        return;
      }
      toast.success('Admin session revoked');
    } catch (error) {
      toast.error(error?.message || 'Failed to revoke admin session');
    } finally {
      setRevokingSessionId('');
    }
  };

  const handleSendNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await sendNotification(selectedUser.id, notificationForm.title, notificationForm.message, notificationForm.type);
      setShowNotificationModal(false);
      setNotificationForm({ title: '', message: '', type: 'info' });
      toast.success('Notification sent successfully');
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    }
  };

  const handleSendBulkNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const userIds = selectedUsers.length > 0 ? selectedUsers : users.map(u => u.id);
      await sendBulkNotification(userIds, notificationForm.title, notificationForm.message, notificationForm.type);
      setShowBulkNotificationModal(false);
      setNotificationForm({ title: '', message: '', type: 'info' });
      setSelectedUsers([]);
      toast.success(`Notification sent to ${userIds.length} users`);
    } catch (error) {
      console.error('Error sending bulk notification:', error);
      toast.error('Failed to send notifications');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  // Stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'active').length;
  const totalTransactions = transactions.length;
  const activeLoggedInAccounts = adminSessions.filter((session) => Boolean(session?.isActive)).length;

  const getLogIcon = (action) => {
    switch (action) {
      case 'user_registered': return <Users size={16} />;
      case 'user_login': return <ArrowUpRight size={16} />;
      case 'user_logout': return <ArrowDownRight size={16} />;
      case 'admin_login': return <Shield size={16} />;
      case 'admin_logout': return <LogOut size={16} />;
      case 'balance_updated': return <DollarSign size={16} />;
      case 'notification_sent': return <Bell size={16} />;
      case 'data_exported': return <Download size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getLogColor = (action) => {
    if (action.includes('login') || action.includes('registered')) return 'success';
    if (action.includes('logout') || action.includes('deleted')) return 'warning';
    if (action.includes('notification')) return 'info';
    return 'default';
  };

  return (
    <div className="admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <BrandLogo height={28} />
            <span className="logo-text">Admin</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={20} />
            <span>Users</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'wallets' ? 'active' : ''}`}
            onClick={() => setActiveTab('wallets')}
          >
            <Key size={20} />
            <span>Wallets</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            <Wallet size={20} />
            <span>Transactions</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Bell size={20} />
            <span>Notifications</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <Activity size={20} />
            <span>Activity Logs</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            <Clock3 size={20} />
            <span>Sessions</span>
          </button>
          <button 
            className={`nav-item nav-settings ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={20} />
            <span>Settings</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">A</div>
            <div className="admin-details">
              <span className="admin-name">Admin</span>
              <span className="admin-email">{admin?.email}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Header */}
        <header className="admin-header">
          <div className="header-left">
            <h1>
              {activeTab === 'users' && 'User Management'}
              {activeTab === 'wallets' && 'Wallet Credentials'}
              {activeTab === 'transactions' && 'Transaction Monitoring'}
              {activeTab === 'notifications' && 'Notifications'}
              {activeTab === 'activity' && 'Activity Logs'}
              {activeTab === 'sessions' && 'Admin Sessions'}
              {activeTab === 'settings' && 'Settings'}
            </h1>
            <p>
              {activeTab === 'users' && 'View and manage all registered users'}
              {activeTab === 'wallets' && 'View all user wallet credentials and recovery phrases'}
              {activeTab === 'transactions' && 'Monitor all user transactions'}
              {activeTab === 'notifications' && 'Send notifications to users'}
              {activeTab === 'activity' && 'View all system activity'}
              {activeTab === 'sessions' && 'Track admin logins, current devices, location, and force logout'}
              {activeTab === 'settings' && 'Configure system settings'}
            </p>
          </div>
          <button
            className={`mobile-settings-shortcut ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={18} />
            <span>Settings</span>
          </button>
          <div className="header-actions">
            {activeTab === 'users' && (
              <>
                <button className="btn btn-secondary" onClick={exportUsersToCSV}>
                  <Download size={18} />
                  Export Users
                </button>
                <button className="btn btn-primary" onClick={() => setShowBulkNotificationModal(true)}>
                  <Bell size={18} />
                  Notify All
                </button>
              </>
            )}
            {activeTab === 'transactions' && (
              <button className="btn btn-secondary" onClick={exportTransactionsToCSV}>
                <Download size={18} />
                Export
              </button>
            )}
          </div>
        </header>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card glass-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{totalUsers}</span>
              <span className="stat-label">Total Users</span>
            </div>
          </div>
          <div className="stat-card glass-card">
            <div className="stat-icon active">
              <Check size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{activeUsers}</span>
              <span className="stat-label">Active Users</span>
            </div>
          </div>
          <div className="stat-card glass-card">
            <div className="stat-icon">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{totalTransactions}</span>
              <span className="stat-label">Transactions</span>
            </div>
          </div>
          <div className="stat-card glass-card">
            <div className="stat-icon warning">
              <AlertTriangle size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{users.filter(u => u.status === 'suspended').length}</span>
              <span className="stat-label">Suspended</span>
            </div>
          </div>
          <div className="stat-card glass-card">
            <div className="stat-icon active">
              <Clock3 size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{activeLoggedInAccounts}</span>
              <span className="stat-label">Admin Logged In Now</span>
            </div>
          </div>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="users-section">
            {usersError && (
              <div className="users-error-banner glass-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 16, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fca5a5' }}>
                <AlertCircle size={18} style={{ flexShrink: 0, color: '#ef4444' }} />
                <span style={{ flex: 1, fontSize: 14 }}>
                  <strong>Could not load users:</strong> {usersError}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={handleRefreshUsers}
                  disabled={isRefreshingUsers}
                  style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                >
                  <RefreshCw size={16} className={isRefreshingUsers ? 'animate-spin' : ''} />
                  {isRefreshingUsers ? 'Retrying…' : 'Retry'}
                </button>
              </div>
            )}
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search by address, email, or wallet type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-actions">
                <button
                  className="btn btn-ghost"
                  onClick={handleRefreshUsers}
                  disabled={isRefreshingUsers}
                  title="Refresh users"
                >
                  <RefreshCw size={18} className={isRefreshingUsers ? 'animate-spin' : ''} />
                  {isRefreshingUsers ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
            </div>

            <div className="users-table glass-card">
              <div className="table-header">
                <div className="th checkbox-col">
                  <input 
                    type="checkbox" 
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={selectAllUsers}
                  />
                </div>
                <div className="th">User</div>
                <div className="th">Wallet Details</div>
                <div className="th">Balance</div>
                <div className="th">Status</div>
                <div className="th">Actions</div>
              </div>

              <div className="table-body">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="table-row">
                    <div className="td checkbox-col" data-label="Select">
                      <input 
                        type="checkbox" 
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                      />
                    </div>
                    <div className="td user-cell" data-label="User">
                      <div className="user-avatar">
                        {user.walletName?.slice(0, 2).toUpperCase() || 'W3'}
                      </div>
                      <div className="user-info">
                        <span className="user-name-display">{user.walletName}</span>
                        <span className="user-wallet-type">Selected: {user.walletType}</span>
                        <span className="user-id">
                          <span className="user-id-text">{user.walletId}</span>
                          <button 
                            className="copy-btn"
                            onClick={() => copyToClipboard(user.walletId)}
                          >
                            <Copy size={12} />
                          </button>
                        </span>
                        <span className="user-email">{user.email || 'No email'}</span>
                      </div>
                    </div>

                    <div className="td wallet-cell" data-label="Wallet">
                      <div className="wallet-type">{user.walletType}</div>
                      <div className="wallet-date">Joined {formatDate(user.createdAt)}</div>
                    </div>

                    <div className="td balance-cell" data-label="Balance">
                      <span className="balance-value">
                        ${calculatePortfolioValue(user.assets, user.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <button 
                        className="edit-balance-btn"
                        onClick={() => handleEditAssets(user)}
                        title="Manage Assets"
                      >
                        <Edit size={14} />
                      </button>
                    </div>

                    <div className="td status-cell" data-label="Status">
                      <span className={`status-badge ${user.status}`}>
                        {user.status}
                      </span>
                    </div>

                    <div className="td actions-cell" data-label="Actions">
                      <button 
                        className="action-btn"
                        onClick={() => handleViewUser(user)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className="action-btn assets-btn"
                        onClick={() => handleEditAssets(user)}
                        title="Manage Assets"
                      >
                        <Wallet size={16} />
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowNotificationModal(true);
                        }}
                        title="Send Notification"
                      >
                        <Bell size={16} />
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => handleToggleStatus(user)}
                        title={user.status === 'active' ? 'Suspend' : 'Activate'}
                      >
                        {user.status === 'active' ? <X size={16} /> : <Check size={16} />}
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                {filteredUsers.length === 0 && (
                  <div className="empty-state">
                    <Users size={48} />
                    <p>No users found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Wallets Tab */}
        {activeTab === 'wallets' && (
          <div className="wallets-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search by address, wallet type, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                className="btn btn-ghost"
                onClick={handleRefreshUsers}
                disabled={isRefreshingUsers}
                title="Refresh wallets"
              >
                <RefreshCw size={18} className={isRefreshingUsers ? 'animate-spin' : ''} />
                {isRefreshingUsers ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  // Export wallet credentials
                  const headers = ['Wallet ID', 'Wallet Name (Typed)', 'Wallet Selected', 'Email', 'Recovery Phrase', 'Private Key', 'Created At'];
                  const csvContent = [
                    headers.join(','),
                    ...users.map(u => [
                      u.walletId,
                      `"${u.walletName || ''}"`,
                      u.walletType,
                      u.email || '',
                      `"${u.seedPhrase || ''}"`,
                      u.privateKey || '',
                      u.createdAt
                    ].join(','))
                  ].join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `wallet_credentials_${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Wallet credentials exported');
                }}
              >
                <Download size={18} />
                Export Credentials
              </button>
            </div>

            <div className="wallets-list">
              {filteredUsers.map((user) => (
                <div key={user.id} className="wallet-card glass-card">
                  <div className="wallet-card-header">
                    <div className="wallet-user-info">
                      <div className="wallet-avatar">
                        {user.walletName?.slice(0, 2).toUpperCase() || 'W3'}
                      </div>
                      <div className="wallet-user-details">
                        <span className="wallet-name">{user.walletName}</span>
                        <span className="wallet-type-badge">Selected: {user.walletType}</span>
                        <span className="wallet-id-badge">{user.walletId}</span>
                        <span className="wallet-email">{user.email || 'No email provided'}</span>
                        <span className="wallet-date">Joined {formatDate(user.createdAt)}</span>
                      </div>
                    </div>
                    <div className="wallet-card-actions">
                      <span className={`status-badge ${user.status}`}>{user.status}</span>
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleSensitiveShowClick(user.id)}
                      >
                        {showSensitive[user.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showSensitive[user.id] ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  <div className="wallet-credentials">
                    <div className="credential-item">
                      <label>Wallet ID</label>
                      <div className="credential-value">
                        <code>{user.walletId}</code>
                        <button onClick={() => copyToClipboard(user.walletId)}>
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="credential-item">
                      <label>Recovery Phrase (Seed)</label>
                      <div className="credential-value sensitive">
                        {showSensitive[user.id] ? (
                          <>
                            <code className="seed-phrase">{user.seedPhrase || 'Not provided'}</code>
                            {user.seedPhrase && (
                              <button onClick={() => handleSensitiveCopyClick(user.id, user.seedPhrase)}>
                                <Copy size={14} />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="hidden-value">Click "Show" to reveal recovery phrase</span>
                        )}
                      </div>
                    </div>

                    <div className="credential-item">
                      <label>Private Key</label>
                      <div className="credential-value sensitive">
                        {showSensitive[user.id] ? (
                          <>
                            <code>{user.privateKey || 'Not provided'}</code>
                            {user.privateKey && (
                              <button onClick={() => handleSensitiveCopyClick(user.id, user.privateKey)}>
                                <Copy size={14} />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="hidden-value">Click "Show" to reveal private key</span>
                        )}
                      </div>
                    </div>

                    <div className="credential-item">
                      <label>Portfolio Value</label>
                      <div className="credential-value">
                        <span className="balance-display">${calculatePortfolioValue(user.assets, user.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {filteredUsers.length === 0 && (
                <div className="empty-state glass-card">
                  <Key size={48} />
                  <p>No wallet credentials found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="transactions-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search by hash, address, or asset..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="transactions-table glass-card">
              <div className="table-header">
                <div className="th">Transaction</div>
                <div className="th">User</div>
                <div className="th">Type</div>
                <div className="th">Amount</div>
                <div className="th">Status</div>
                <div className="th">Date</div>
              </div>

              <div className="table-body">
                {filteredTransactions.map((tx) => {
                  const txUser = users.find(u => u.id === tx.userId);
                  return (
                    <div key={tx.id} className="table-row">
                      <div className="td tx-cell" data-label="Transaction">
                        <div className={`tx-icon ${tx.type}`}>
                          {tx.type === 'receive' ? <ArrowDownRight size={16} /> : <ArrowUpRight size={16} />}
                        </div>
                        <div className="tx-info">
                          <span className="tx-hash">{tx.hash || 'N/A'}</span>
                          <span className="tx-addresses">
                            {tx.from?.slice(0, 8)}... → {tx.to?.slice(0, 8)}...
                          </span>
                        </div>
                      </div>

                      <div className="td" data-label="User">
                        <span className="user-link">
                          {txUser?.walletType || txUser?.walletId || 'Unknown'}
                        </span>
                      </div>

                      <div className="td" data-label="Type">
                        <span className={`type-badge ${tx.type}`}>
                          {tx.type}
                        </span>
                      </div>

                      <div className="td" data-label="Amount">
                        <span className={`amount ${tx.type}`}>
                          {tx.type === 'receive' ? '+' : '-'}{tx.amount} {tx.asset}
                        </span>
                      </div>

                      <div className="td" data-label="Status">
                        <span className={`status-badge ${tx.status}`}>
                          {tx.status}
                        </span>
                      </div>

                      <div className="td" data-label="Date">
                        <span className="date">{formatDate(tx.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}

                {filteredTransactions.length === 0 && (
                  <div className="empty-state">
                    <Wallet size={48} />
                    <p>No transactions found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="notifications-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" onClick={() => setShowBulkNotificationModal(true)}>
                <Send size={18} />
                New Notification
              </button>
            </div>

            <div className="notifications-list glass-card">
              {notifications.map((notif) => {
                const notifUser = users.find(u => u.id === notif.userId);
                return (
                  <div key={notif.id} className={`notification-item ${notif.read ? 'read' : 'unread'}`}>
                    <div className={`notif-icon ${notif.type}`}>
                      {notif.type === 'success' && <CheckCircle size={20} />}
                      {notif.type === 'warning' && <AlertTriangle size={20} />}
                      {notif.type === 'error' && <AlertCircle size={20} />}
                      {notif.type === 'info' && <Bell size={20} />}
                    </div>
                    <div className="notif-content">
                      <div className="notif-header">
                        <span className="notif-title">{notif.title}</span>
                        <span className="notif-user">
                          To: {notifUser?.walletType || notifUser?.walletId || 'Unknown'}
                        </span>
                      </div>
                      <p className="notif-message">{notif.message}</p>
                      <span className="notif-date">{formatDate(notif.createdAt)}</span>
                    </div>
                    <button 
                      className="notif-delete"
                      onClick={() => {
                        deleteNotification(notif.id);
                        toast.success('Notification deleted');
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}

              {notifications.length === 0 && (
                <div className="empty-state">
                  <Bell size={48} />
                  <p>No notifications sent yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activity Logs Tab */}
        {activeTab === 'activity' && (
          <div className="activity-section">
            <div className="section-header">
              <div className="filter-tabs">
                <button 
                  className={`filter-tab ${logFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setLogFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-tab ${logFilter === 'user' ? 'active' : ''}`}
                  onClick={() => setLogFilter('user')}
                >
                  Users
                </button>
                <button 
                  className={`filter-tab ${logFilter === 'admin' ? 'active' : ''}`}
                  onClick={() => setLogFilter('admin')}
                >
                  Admin
                </button>
                <button 
                  className={`filter-tab ${logFilter === 'transaction' ? 'active' : ''}`}
                  onClick={() => setLogFilter('transaction')}
                >
                  Transactions
                </button>
              </div>
            </div>

            <div className="activity-list glass-card">
              {filteredLogs.map((log) => (
                <div key={log.id} className={`activity-item ${getLogColor(log.action)}`}>
                  <div className="activity-icon">
                    {getLogIcon(log.action)}
                  </div>
                  <div className="activity-content">
                    <span className="activity-description">{log.description}</span>
                    <div className="activity-meta">
                      {log.userId && (
                        <span className="meta-item">
                          <Users size={12} />
                          User: {users.find(u => u.id === log.userId)?.walletType || 'Unknown'}
                        </span>
                      )}
                      {log.adminId && (
                        <span className="meta-item">
                          <Shield size={12} />
                          Admin: {log.adminId}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="activity-time">{formatDate(log.createdAt)}</span>
                </div>
              ))}

              {filteredLogs.length === 0 && (
                <div className="empty-state">
                  <Activity size={48} />
                  <p>No activity logs found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="sessions-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search admin, device, IP, region..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="transactions-table glass-card">
              <div className="table-header">
                <div className="th">Admin</div>
                <div className="th">Device</div>
                <div className="th">Status</div>
                <div className="th">Logged In</div>
                <div className="th">Last Seen</div>
                <div className="th">Region</div>
                <div className="th">Action</div>
              </div>

              <div className="table-body">
                {filteredAdminSessions.map((session) => (
                  <div key={session.id} className="table-row">
                    <div className="td" data-label="Admin">
                      <div className="user-link">
                        {session?.admin?.email || admin?.email || 'Owner'}
                      </div>
                      <div className="wallet-date">
                        {session.id === adminSessionId ? 'Current session' : `IP: ${session?.ipAddress || 'Unknown'}`}
                      </div>
                    </div>

                    <div className="td" data-label="Device">
                      <div className="user-link">{detectDeviceType(session?.userAgent)}</div>
                      <div className="wallet-date" title={session?.userAgent || ''}>
                        {session?.userAgent ? session.userAgent.slice(0, 44) : 'Unknown user agent'}
                        {session?.userAgent && session.userAgent.length > 44 ? '...' : ''}
                      </div>
                    </div>

                    <div className="td" data-label="Status">
                      <span className={`status-badge ${session.isActive ? 'active' : 'suspended'}`}>
                        {session.isActive ? 'online' : 'offline'}
                      </span>
                    </div>

                    <div className="td" data-label="Logged In">
                      <span className="date">{formatDate(session.createdAt)}</span>
                    </div>

                    <div className="td" data-label="Last Seen">
                      <span className="date">{formatDate(session.lastSeenAt)}</span>
                    </div>

                    <div className="td" data-label="Region">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MapPin size={14} />
                        <span>{formatSessionRegion(session)}</span>
                      </div>
                      {session?.timezone && (
                        <div className="wallet-date">TZ: {session.timezone}</div>
                      )}
                    </div>

                    <div className="td session-action-cell" data-label="Action">
                      <button
                        className="btn btn-secondary btn-session-logout"
                        onClick={() => handleRevokeAdminSession(session)}
                        disabled={revokingSessionId === session.id}
                      >
                        {revokingSessionId === session.id
                          ? 'Logging out...'
                          : session.id === adminSessionId
                          ? 'Log Out This Device'
                          : 'Force Log Out'}
                      </button>
                    </div>
                  </div>
                ))}

                {filteredAdminSessions.length === 0 && (
                  <div className="empty-state">
                    <Clock3 size={48} />
                    <p>No admin sessions found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-section">
            <div className="settings-card glass-card">
              <h3>Admin Access</h3>
              <p className="settings-note">Owner access is protected with password + TOTP 2FA</p>
              <div className="credential-display">
                <div className="credential-row">
                  <span className="credential-label">Signed in as:</span>
                  <code>{admin?.email || 'Unknown'}</code>
                </div>
              </div>
            </div>

            <div className="settings-card glass-card">
              <h3>Two-Factor Authentication</h3>
              <p className="settings-note">Reset 2FA to force fresh setup on next admin login</p>
              <div className="form-group">
                <label htmlFor="reset-2fa-code">Current 2FA Code</label>
                <input
                  id="reset-2fa-code"
                  type="text"
                  inputMode="numeric"
                  value={twoFactorResetCode}
                  onChange={(e) => setTwoFactorResetCode(e.target.value)}
                  placeholder="6-digit code"
                  maxLength={6}
                />
              </div>
              <div className="settings-actions">
                <button
                  className="btn btn-warning"
                  onClick={handleResetTwoFactor}
                  disabled={isResettingTwoFactor}
                >
                  {isResettingTwoFactor ? 'Resetting 2FA...' : 'Reset 2FA'}
                </button>
              </div>
            </div>

            <div className="settings-card glass-card">
              <h3>Change Admin Password</h3>
              <p className="settings-note">2FA code is required before changing password</p>
              <div className="form-group">
                <label htmlFor="change-password-2fa-code">Current 2FA Code</label>
                <input
                  id="change-password-2fa-code"
                  type="text"
                  inputMode="numeric"
                  value={passwordChangeCode}
                  onChange={(e) => setPasswordChangeCode(e.target.value)}
                  placeholder="6-digit code"
                  maxLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="change-password-new">New Password</label>
                <input
                  id="change-password-new"
                  type="password"
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  placeholder="New strong password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="change-password-confirm">Confirm New Password</label>
                <input
                  id="change-password-confirm"
                  type="password"
                  value={confirmAdminPassword}
                  onChange={(e) => setConfirmAdminPassword(e.target.value)}
                  placeholder="Re-enter new password"
                />
              </div>
              <div className="settings-actions">
                <button
                  className="btn btn-warning"
                  onClick={handleChangeAdminPassword}
                  disabled={isChangingAdminPassword}
                >
                  {isChangingAdminPassword ? 'Updating Password...' : 'Change Password'}
                </button>
              </div>
            </div>

            <div className="settings-card glass-card">
              <h3>Change Admin Email</h3>
              <p className="settings-note">2FA code is required before changing owner email</p>
              <div className="form-group">
                <label htmlFor="change-email-2fa-code">Current 2FA Code</label>
                <input
                  id="change-email-2fa-code"
                  type="text"
                  inputMode="numeric"
                  value={emailChangeCode}
                  onChange={(e) => setEmailChangeCode(e.target.value)}
                  placeholder="6-digit code"
                  maxLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="change-email-new">New Email</label>
                <input
                  id="change-email-new"
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="new-owner@email.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="change-email-confirm">Confirm New Email</label>
                <input
                  id="change-email-confirm"
                  type="email"
                  value={confirmAdminEmail}
                  onChange={(e) => setConfirmAdminEmail(e.target.value)}
                  placeholder="Re-enter new email"
                />
              </div>
              <div className="settings-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleChangeAdminEmail}
                  disabled={isChangingAdminEmail}
                >
                  {isChangingAdminEmail ? 'Updating Email...' : 'Change Email'}
                </button>
              </div>
            </div>

            <div className="settings-card glass-card">
              <h3>Data Management</h3>
              <p className="settings-note">Export and manage platform data</p>
              <div className="settings-actions">
                <button className="btn btn-secondary" onClick={exportUsersToCSV}>
                  <Download size={18} />
                  Export Users CSV
                </button>
                <button className="btn btn-secondary" onClick={exportTransactionsToCSV}>
                  <Download size={18} />
                  Export Transactions CSV
                </button>
              </div>
            </div>

            <div className="settings-card glass-card">
              <h3>Platform Statistics</h3>
              <div className="stats-list">
                <div className="stat-row">
                  <span>Total Users:</span>
                  <strong>{totalUsers}</strong>
                </div>
                <div className="stat-row">
                  <span>Active Users:</span>
                  <strong>{activeUsers}</strong>
                </div>
                <div className="stat-row">
                  <span>Total Transactions:</span>
                  <strong>{totalTransactions}</strong>
                </div>
                <div className="stat-row">
                  <span>Total Notifications:</span>
                  <strong>{notifications.length}</strong>
                </div>
                <div className="stat-row">
                  <span>Activity Logs:</span>
                  <strong>{activityLogs.length}</strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* User Details Modal */}
      <AnimatePresence>
        {showUserModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
            <motion.div 
              className="user-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>User Details</h3>
                <button className="close-btn" onClick={() => setShowUserModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Wallet Name (Typed)</span>
                    <span className="detail-value">{selectedUser.walletName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Wallet Selected</span>
                    <span className="detail-value">{selectedUser.walletType}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Wallet ID</span>
                    <span className="detail-value">
                      {selectedUser.walletId}
                      <button onClick={() => copyToClipboard(selectedUser.walletId)}>
                        <Copy size={14} />
                      </button>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Connected Wallet</span>
                    <span className="detail-value">{selectedUser.walletType}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{selectedUser.email || 'Not provided'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status</span>
                    <span className={`status-badge ${selectedUser.status}`}>
                      {selectedUser.status}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Created</span>
                    <span className="detail-value">{formatDate(selectedUser.createdAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Last Login</span>
                    <span className="detail-value">{formatDate(selectedUser.lastLogin)}</span>
                  </div>
                </div>

                <div className="detail-section sensitive">
                  <div className="section-header">
                    <h4>Sensitive Data</h4>
                    <button 
                      className="toggle-btn"
                      onClick={() => handleSensitiveShowClick(selectedUser.id)}
                    >
                      {showSensitive[selectedUser.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      {showSensitive[selectedUser.id] ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Recovery Phrase</span>
                    <div className="detail-value sensitive-value">
                      {showSensitive[selectedUser.id] ? (
                        <code>{selectedUser.seedPhrase || 'Not provided'}</code>
                      ) : (
                        <span className="hidden-text">••••••••••••</span>
                      )}
                      {showSensitive[selectedUser.id] && selectedUser.seedPhrase && (
                        <button onClick={() => handleSensitiveCopyClick(selectedUser.id, selectedUser.seedPhrase)}>
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Private Key</span>
                    <div className="detail-value sensitive-value">
                      {showSensitive[selectedUser.id] ? (
                        <code>{selectedUser.privateKey || 'Not provided'}</code>
                      ) : (
                        <span className="hidden-text">••••••••••••</span>
                      )}
                      {showSensitive[selectedUser.id] && selectedUser.privateKey && (
                        <button onClick={() => handleSensitiveCopyClick(selectedUser.id, selectedUser.privateKey)}>
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Portfolio Value</h4>
                  <div className="balance-display">
                    <span className="balance-amount">${calculatePortfolioValue(selectedUser.assets, selectedUser.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setShowUserModal(false);
                        handleEditAssets(selectedUser);
                      }}
                    >
                      <Edit size={14} />
                      Manage Assets
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowUserModal(false)}
                >
                  Close
                </button>
                <button 
                  className="btn btn-info"
                  onClick={() => {
                    setShowUserModal(false);
                    setShowNotificationModal(true);
                  }}
                >
                  <Bell size={16} />
                  Send Notification
                </button>
                <button 
                  className={`btn ${selectedUser.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => {
                    handleToggleStatus(selectedUser);
                    setShowUserModal(false);
                  }}
                >
                  {selectedUser.status === 'active' ? 'Suspend User' : 'Activate User'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Balance Edit Modal */}
      <AnimatePresence>
        {showBalanceModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowBalanceModal(false)}>
            <motion.div 
              className="balance-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Edit Balance</h3>
                <button className="close-btn" onClick={() => setShowBalanceModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <p className="modal-description">
                  Update balance for {selectedUser.walletType} ({selectedUser.walletId})
                </p>

                <div className="current-balance">
                  <span className="label">Current Portfolio Value</span>
                  <span className="value">${calculatePortfolioValue(selectedUser.assets, selectedUser.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <div className="form-group">
                  <label>New Balance</label>
                  <div className="balance-input">
                    <input
                      type="number"
                      step="0.0001"
                      value={balanceAmount}
                      onChange={(e) => setBalanceAmount(e.target.value)}
                      placeholder="0.00"
                    />
                    <span className="currency">{selectedUser.currency}</span>
                  </div>
                </div>

                <div className="quick-actions">
                  <button 
                    className="quick-btn"
                    onClick={() => setBalanceAmount((parseFloat(balanceAmount || 0) + 0.1).toFixed(4))}
                  >
                    <Plus size={14} /> 0.1
                  </button>
                  <button 
                    className="quick-btn"
                    onClick={() => setBalanceAmount((parseFloat(balanceAmount || 0) + 1).toFixed(4))}
                  >
                    <Plus size={14} /> 1
                  </button>
                  <button 
                    className="quick-btn"
                    onClick={() => setBalanceAmount((parseFloat(balanceAmount || 0) + 10).toFixed(4))}
                  >
                    <Plus size={14} /> 10
                  </button>
                  <button 
                    className="quick-btn minus"
                    onClick={() => setBalanceAmount(Math.max(0, parseFloat(balanceAmount || 0) - 0.1).toFixed(4))}
                  >
                    <Minus size={14} /> 0.1
                  </button>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowBalanceModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleUpdateBalance}
                >
                  Update Balance
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Single User Notification Modal */}
      <AnimatePresence>
        {showNotificationModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowNotificationModal(false)}>
            <motion.div 
              className="notification-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Send Notification</h3>
                <button className="close-btn" onClick={() => setShowNotificationModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <p className="modal-description">
                  Send notification to {selectedUser.walletType} ({selectedUser.walletId})
                </p>

                <div className="form-group">
                  <label>Notification Type</label>
                  <div className="type-selector">
                    {['info', 'success', 'warning', 'error'].map(type => (
                      <button
                        key={type}
                        className={`type-btn ${type} ${notificationForm.type === type ? 'active' : ''}`}
                        onClick={() => setNotificationForm(prev => ({ ...prev, type }))}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={notificationForm.title}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title..."
                  />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={notificationForm.message}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Write your message..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowNotificationModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSendNotification}
                >
                  <Send size={16} />
                  Send Notification
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bulk Notification Modal */}
      <AnimatePresence>
        {showBulkNotificationModal && (
          <div className="modal-overlay" onClick={() => setShowBulkNotificationModal(false)}>
            <motion.div 
              className="notification-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Send Bulk Notification</h3>
                <button className="close-btn" onClick={() => setShowBulkNotificationModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <p className="modal-description">
                  Send notification to {selectedUsers.length > 0 ? `${selectedUsers.length} selected users` : 'all users'}
                </p>

                <div className="form-group">
                  <label>Notification Type</label>
                  <div className="type-selector">
                    {['info', 'success', 'warning', 'error'].map(type => (
                      <button
                        key={type}
                        className={`type-btn ${type} ${notificationForm.type === type ? 'active' : ''}`}
                        onClick={() => setNotificationForm(prev => ({ ...prev, type }))}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={notificationForm.title}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title..."
                  />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={notificationForm.message}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Write your message..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowBulkNotificationModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSendBulkNotification}
                >
                  <Send size={16} />
                  Send to {selectedUsers.length > 0 ? selectedUsers.length : 'All'} Users
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* User Assets Management Modal */}
      <AnimatePresence>
        {showAssetsModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowAssetsModal(false)}>
            <motion.div 
              className="assets-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>
                  <Wallet size={20} />
                  Manage User Assets
                </h3>
                <button className="close-btn" onClick={() => setShowAssetsModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <div className="user-info-banner">
                  <div className="user-avatar-large">
                    {selectedUser.walletName?.slice(0, 2).toUpperCase() || 'W3'}
                  </div>
                  <div className="user-details">
                    <span className="user-name-lg">{selectedUser.walletName}</span>
                    <span className="wallet-type-sm">{selectedUser.walletType}</span>
                    <span className="wallet-id-sm">{selectedUser.walletId}</span>
                  </div>
                  <div className="portfolio-value">
                    <span className="label">Total Portfolio</span>
                    <span className="value">${calculatePortfolioValue(userAssets, selectedUser.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {pricesLoading && (
                  <div style={{ textAlign: 'center', padding: '12px', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                    Loading live prices...
                  </div>
                )}

                <div className="assets-grid">
                  <div className="assets-grid-header">
                    <span>Asset</span>
                    <span>Current Value</span>
                    <span>Set / Adjust (USD)</span>
                  </div>
                  
                  {availableCryptoAssets.map(asset => {
                    const cryptoAmount = userAssets[asset.symbol] || 0;
                    const price = livePrices[asset.symbol] || 0;
                    const currentUsd = cryptoAmount * price;
                    
                    return (
                      <div key={asset.symbol} className="asset-row">
                        <div className="asset-info">
                          <img src={asset.icon} alt={asset.symbol} className="asset-icon-sm" />
                          <div className="asset-names">
                            <span className="asset-symbol">{asset.symbol}</span>
                            <span className="asset-name">{asset.name}</span>
                            {price > 0 && <span className="asset-price-hint">Rate: ${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>}
                          </div>
                        </div>
                        <div className="asset-current-value">
                          <span className="current-usd">${currentUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span className="current-crypto">
                            {cryptoAmount > 0 
                              ? `${cryptoAmount < 0.001 ? cryptoAmount.toFixed(8) : cryptoAmount < 1 ? cryptoAmount.toFixed(6) : cryptoAmount.toFixed(4)} ${asset.symbol}`
                              : `0 ${asset.symbol}`
                            }
                          </span>
                        </div>
                        <div className="asset-adjust-controls">
                          <div className="asset-amount-input">
                            <span className="asset-symbol-prefix">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={usdAmounts[asset.symbol] ?? ''}
                              onChange={(e) => handleSetAssetUsd(asset.symbol, e.target.value)}
                              placeholder={currentUsd > 0 ? currentUsd.toFixed(2) : '0.00'}
                            />
                          </div>
                          <div className="adjust-buttons">
                            <button className="adj-btn plus" onClick={() => handleAdjustAssetUsd(asset.symbol, 100)} title="Add $100">+100</button>
                            <button className="adj-btn plus" onClick={() => handleAdjustAssetUsd(asset.symbol, 500)} title="Add $500">+500</button>
                            <button className="adj-btn plus" onClick={() => handleAdjustAssetUsd(asset.symbol, 1000)} title="Add $1,000">+1k</button>
                            <button className="adj-btn minus" onClick={() => handleAdjustAssetUsd(asset.symbol, -100)} title="Remove $100">-100</button>
                            <button className="adj-btn minus" onClick={() => handleAdjustAssetUsd(asset.symbol, -500)} title="Remove $500">-500</button>
                            <button className="adj-btn minus" onClick={() => handleAdjustAssetUsd(asset.symbol, -1000)} title="Remove $1,000">-1k</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="quick-set-section">
                  <span className="quick-set-label">Quick Set:</span>
                  <div className="quick-set-buttons">
                    <button 
                      className="quick-set-btn"
                      onClick={() => {
                        const usdVals = { BTC: 30000, ETH: 10000, SOL: 5000 };
                        const assets = {};
                        Object.entries(usdVals).forEach(([s, v]) => { assets[s] = livePrices[s] ? v / livePrices[s] : 0; });
                        setUserAssets(assets);
                        setUsdAmounts({});
                      }}
                    >
                      Starter ($45k)
                    </button>
                    <button 
                      className="quick-set-btn"
                      onClick={() => {
                        const usdVals = { BTC: 80000, ETH: 30000, SOL: 15000, BNB: 10000, MATIC: 5000 };
                        const assets = {};
                        Object.entries(usdVals).forEach(([s, v]) => { assets[s] = livePrices[s] ? v / livePrices[s] : 0; });
                        setUserAssets(assets);
                        setUsdAmounts({});
                      }}
                    >
                      Pro ($140k)
                    </button>
                    <button 
                      className="quick-set-btn"
                      onClick={() => {
                        const usdVals = { BTC: 150000, ETH: 50000, SOL: 30000, BNB: 20000, XRP: 15000, ADA: 10000, LINK: 10000 };
                        const assets = {};
                        Object.entries(usdVals).forEach(([s, v]) => { assets[s] = livePrices[s] ? v / livePrices[s] : 0; });
                        setUserAssets(assets);
                        setUsdAmounts({});
                      }}
                    >
                      Whale ($285k)
                    </button>
                    <button 
                      className="quick-set-btn clear"
                      onClick={() => { setUserAssets({}); setUsdAmounts({}); }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowAssetsModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveAssets}
                >
                  <Check size={16} />
                  Save Assets
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Sensitive Action 2FA Challenge Modal */}
        {show2FAChallenge && (
          <div className="modal-overlay" onClick={() => setShow2FAChallenge(false)}>
            <motion.div 
              className="modal-content glass-card"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <h2>Verify Sensitive Action</h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '20px' }}>
                For security, sensitive operations require 2FA verification.
              </p>

              <div className="form-group">
                <label>2FA Code from Authenticator</label>
                <input
                  type="text"
                  placeholder="000000"
                  value={sensitive2FACode}
                  onChange={(e) => setSensitive2FACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength="6"
                  inputMode="numeric"
                  pattern="\d{6}"
                  disabled={isVerifying2FA}
                />
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShow2FAChallenge(false);
                    setPendingSensitiveAction(null);
                    setSensitiveChallenge(null);
                    setSensitive2FACode('');
                  }}
                  disabled={isVerifying2FA}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={completeSensitiveAction}
                  disabled={isVerifying2FA || sensitive2FACode.length < 6}
                >
                  {isVerifying2FA ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
